import {
  Button,
  FormControl,
  FormLabel,
  HStack,
  Select,
  Text,
  Textarea,
  useToast,
  VStack,
} from '@chakra-ui/react';
import {
  createGoogleAlertRelevancePromptConfigVersion,
  DEFAULT_GOOGLE_ALERT_CATEGORIES,
  DEFAULT_GOOGLE_ALERT_RELEVANCE_PROMPT,
  DEFAULT_GOOGLE_ALERT_SUMMARY_PROMPT,
  getGoogleAlertRelevancePromptConfigVersion,
  getLatestGoogleAlertRelevancePromptConfig,
  listGoogleAlertRelevancePromptConfigVersions,
} from 'api/googleAlertRelevancePromptConfigApi';
import ContentBox from 'components/ContentBox';
import { useEffect, useState } from 'react';

function GoogleAlertRelevancePromptConfigAdmin() {
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [summaries, setSummaries] = useState([]);
  const [selectedVersion, setSelectedVersion] = useState('edit');
  const [viewDoc, setViewDoc] = useState(null);
  const [allowedModels, setAllowedModels] = useState([]);
  const [draftRelevancePrompt, setDraftRelevancePrompt] = useState(
    DEFAULT_GOOGLE_ALERT_RELEVANCE_PROMPT
  );
  const [draftSummaryPrompt, setDraftSummaryPrompt] = useState(
    DEFAULT_GOOGLE_ALERT_SUMMARY_PROMPT
  );
  const [draftCategories, setDraftCategories] = useState(
    DEFAULT_GOOGLE_ALERT_CATEGORIES
  );
  const [draftModel, setDraftModel] = useState('claude-haiku-4-5');
  const [saveNote, setSaveNote] = useState('');
  const [saving, setSaving] = useState(false);

  const bootstrap = async () => {
    setLoading(true);
    try {
      let latest = null;
      try {
        latest = await getLatestGoogleAlertRelevancePromptConfig();
      } catch (err) {
        if (err?.response?.status !== 404) {
          throw err;
        }
      }
      const sum = await listGoogleAlertRelevancePromptConfigVersions();
      setSummaries(sum || []);
      setSelectedVersion('edit');
      setViewDoc(null);
      setAllowedModels(latest?.allowedModels || []);
      setDraftRelevancePrompt(
        latest?.relevancePrompt || DEFAULT_GOOGLE_ALERT_RELEVANCE_PROMPT
      );
      setDraftSummaryPrompt(
        latest?.summaryPrompt || DEFAULT_GOOGLE_ALERT_SUMMARY_PROMPT
      );
      setDraftCategories(latest?.categories || DEFAULT_GOOGLE_ALERT_CATEGORIES);
      setDraftModel(latest?.model || 'claude-haiku-4-5');
    } catch (e) {
      toast({
        title: 'Error',
        description: e?.response?.data?.error || e.message,
        status: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    bootstrap();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onVersionChange = async (value) => {
    setSelectedVersion(value);
    if (value === 'edit') {
      setViewDoc(null);
      return;
    }
    const doc = await getGoogleAlertRelevancePromptConfigVersion(Number(value));
    setViewDoc(doc);
    if (doc?.allowedModels?.length) {
      setAllowedModels(doc.allowedModels);
    }
  };

  const onSave = async () => {
    if (!draftRelevancePrompt.trim()) {
      toast({ title: 'Relevance prompt required', status: 'warning' });
      return;
    }
    if (!draftSummaryPrompt.trim()) {
      toast({ title: 'Summary prompt required', status: 'warning' });
      return;
    }
    if (!draftCategories.trim()) {
      toast({ title: 'Categories required', status: 'warning' });
      return;
    }
    if (!draftModel) {
      toast({ title: 'Model required', status: 'warning' });
      return;
    }

    setSaving(true);
    try {
      await createGoogleAlertRelevancePromptConfigVersion({
        relevancePrompt: draftRelevancePrompt.trim(),
        summaryPrompt: draftSummaryPrompt.trim(),
        categories: draftCategories.trim(),
        model: draftModel,
        note: saveNote.trim() || undefined,
      });
      toast({ title: 'Saved new version', status: 'success' });
      setSaveNote('');
      await bootstrap();
    } catch (e) {
      toast({
        title: 'Save failed',
        description: e?.response?.data?.error || e.message,
        status: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  const readOnly = selectedVersion !== 'edit';
  const displayRelevancePrompt = readOnly
    ? viewDoc?.relevancePrompt
    : draftRelevancePrompt;
  const displaySummaryPrompt = readOnly
    ? viewDoc?.summaryPrompt
    : draftSummaryPrompt;
  const displayCategories = readOnly ? viewDoc?.categories : draftCategories;
  const displayModel = readOnly ? viewDoc?.model : draftModel;
  const modelOptions = allowedModels.length
    ? allowedModels
    : displayModel
      ? [displayModel]
      : [];

  return (
    <ContentBox loading={loading} title="Google Alerts">
      <VStack align="stretch" spacing={6}>
        <Text color="gray.600" fontSize="sm">
          Prompts, categories, and Claude model used by the Google Alerts daily
          digest. Saving always creates a new version — the next run uses the
          latest. Past versions are kept for reference and can be viewed
          (read-only) below.
        </Text>
        <HStack>
          <FormControl maxW="280px">
            <FormLabel>Version</FormLabel>
            <Select
              value={selectedVersion}
              onChange={(e) => onVersionChange(e.target.value)}
            >
              <option value="edit">Edit (from latest)</option>
              {(summaries || []).map((s) => (
                <option key={s.version} value={String(s.version)}>
                  v{s.version}
                  {s.note ? ` — ${s.note}` : ''}
                </option>
              ))}
            </Select>
          </FormControl>
        </HStack>
        <FormControl maxW="280px">
          <FormLabel>Model</FormLabel>
          <Select
            value={displayModel || ''}
            onChange={(e) => setDraftModel(e.target.value)}
            isDisabled={readOnly}
          >
            {modelOptions.map((m) => (
              <option key={m} value={m}>
                {m}
              </option>
            ))}
          </Select>
        </FormControl>
        <FormControl>
          <FormLabel>Relevance prompt</FormLabel>
          <Text
            color="gray.500"
            fontSize="sm"
            mb={2}
          >{`Placeholders: {{companyName}}, {{contextBlock}}, {{categories}}`}</Text>
          <Textarea
            value={displayRelevancePrompt || ''}
            onChange={(e) => setDraftRelevancePrompt(e.target.value)}
            isDisabled={readOnly}
            minH="320px"
            fontFamily="mono"
            fontSize="sm"
          />
        </FormControl>
        <FormControl>
          <FormLabel>Article summary prompt</FormLabel>
          <Text
            color="gray.500"
            fontSize="sm"
            mb={2}
          >{`Placeholders: {{url}}, {{title}}, {{companyName}}`}</Text>
          <Textarea
            value={displaySummaryPrompt || ''}
            onChange={(e) => setDraftSummaryPrompt(e.target.value)}
            isDisabled={readOnly}
            minH="160px"
            fontFamily="mono"
            fontSize="sm"
          />
        </FormControl>
        <FormControl>
          <FormLabel>Categories</FormLabel>
          <Textarea
            value={displayCategories || ''}
            onChange={(e) => setDraftCategories(e.target.value)}
            isDisabled={readOnly}
            minH="80px"
          />
        </FormControl>
        {!readOnly && (
          <>
            <FormControl>
              <FormLabel>Version note (optional)</FormLabel>
              <Textarea
                value={saveNote}
                onChange={(e) => setSaveNote(e.target.value)}
                minH="60px"
              />
            </FormControl>
            <Button
              colorScheme="purple"
              onClick={onSave}
              isLoading={saving}
              alignSelf="flex-start"
            >
              Save as new version
            </Button>
          </>
        )}
      </VStack>
    </ContentBox>
  );
}

export default GoogleAlertRelevancePromptConfigAdmin;
